import * as React from "react";
import styled from "styled-components";

import EditableTitle from "../../../App/Common/Components/EditableTitle";
import { ICourseAuthorResolvedData, ILocalizedMessageApi } from "../../../reducers/course-author/types";
import { fullCourseViews } from "../../../utils/courseViews";
import { PreferredLocale } from "../../../utils/locales";
import ChangesTab from "../../pages/CourseAuthor/ChangesTab";
import ContentMigration from "../../pages/CourseAuthor/ContentMigration";
import PreviewTabContent from "../../pages/CourseAuthor/PreviewTab/index";
import SettingsTab from "../../pages/CourseAuthor/SettingsTab";
import { H1 } from "../../utils/Headings";
import ToggleDisplay from "../../utils/ToggleDisplay";
import {
  CourseContentType,
  CourseView,
  DefaultNames,
  ICourseEditOperations,
  ICourseNavigationOperations,
  ICourseUnit,
  IFullCourse,
} from "../types";
import { ITabInformation, TabButtons } from "../../../App/Tabs";

const CourseTabsContainer = styled.div`
  width: ${({ theme }) => theme.primaryTheme.maxWidth};
`;

export interface ICourseTabsProps {
  course: IFullCourse;
  courseId: string;
  currentLocale: PreferredLocale;
  editOperations: ICourseEditOperations;
  initialTab?: Nullable<CourseTabsEnum>;
  migrateContentToHtml: () => void;
  navigationOperations: ICourseNavigationOperations;
  resolve: (key: keyof ICourseAuthorResolvedData, requestData: string[]) => void;
  resolvedData: ICourseAuthorResolvedData;
  unitPath: Array<CourseView<ICourseUnit, IFullCourse>>;
  userId: string;
  localizedMessageApi: ILocalizedMessageApi;
}

export enum CourseTabsEnum {
  Edit = "edit",
  Settings = "settings",
  Changes = "changes",
}

export interface ICourseTabsState {
  displayed: CourseTabsEnum;
}

export default class CourseTabs extends React.Component<ICourseTabsProps, ICourseTabsState> {
  constructor(props: ICourseTabsProps) {
    super(props);
    this.state = {
      displayed: props.initialTab || CourseTabsEnum.Edit,
    };
  }

  public render() {
    const { course, editOperations } = this.props;

    const currentTitle = fullCourseViews.localizedTitle(course, this.props.currentLocale) as string;

    return (
      <>
        <H1>
          <EditableTitle
            addTitle={editOperations.addTitle()}
            modifyTitle={editOperations.modifyTitle()}
            currentTitle={currentTitle}
            defaultTitle={DefaultNames.Course}
            isMainTitle={true}
          />
        </H1>
        <CourseTabsContainer>{this.migrateContentComponent()}</CourseTabsContainer>
      </>
    );
  }

  private migrateContentComponent = () => {
    const { course, migrateContentToHtml } = this.props;
    const shouldMigrateContent = CourseContentType.MarkdownContent === course.state.contentType;
    if (shouldMigrateContent) {
      const migrateToHtmlConfirmation = () => {
        const confirmationMessage =
          "Dear user, we create a backup of this course before starting since" +
          " once this migration is done it can not be reversed." +
          "\n" +
          "Remember changes won't appear to students as long as they have " +
          "not been published. Would you like to continue?";
        if (window.confirm(confirmationMessage)) {
          migrateContentToHtml();
        }
      };
      return <ContentMigration migrateToHtml={migrateToHtmlConfirmation} />;
    }

    const courseTabs: Array<ITabInformation<CourseTabsEnum>> = [
      {
        queryParam: "edit",
        tabItem: CourseTabsEnum.Edit,
        title: "Edit",
      },
      {
        queryParam: "settings",
        tabItem: CourseTabsEnum.Settings,
        title: "Settings",
      },
      {
        queryParam: "changes",
        tabItem: CourseTabsEnum.Changes,
        title: "Changes",
      },
    ];

    return (
      <>
        <TabButtons displayed={this.state.displayed} setSelected={this.selectTab} tabs={courseTabs} />
        {this.renderTab()}
      </>
    );
  };

  private selectTab = (tab: CourseTabsEnum) => () => this.setState({ displayed: tab });

  private renderTab() {
    return (
      <>
        <ToggleDisplay shouldDisplay={this.state.displayed === CourseTabsEnum.Edit}>
          <PreviewTabContent
            course={this.props.course}
            courseId={this.props.courseId}
            editOperations={this.props.editOperations}
            currentLocale={this.props.currentLocale}
            maxTitleWidth={47.5}
            navigationOperations={this.props.navigationOperations}
            userId={this.props.userId}
            unitPath={[]}
          />
        </ToggleDisplay>
        {this.state.displayed === CourseTabsEnum.Settings && (
          <SettingsTab
            course={this.props.course}
            courseId={this.props.courseId}
            currentLocale={this.props.currentLocale}
            editOperations={this.props.editOperations}
            resolve={this.props.resolve}
            resolvedData={this.props.resolvedData}
            userId={this.props.userId}
            localizedMessageApi={this.props.localizedMessageApi}
          />
        )}
        {this.state.displayed === CourseTabsEnum.Changes && (
          <ChangesTab
            course={this.props.course}
            courseId={this.props.courseId}
            editOperations={this.props.editOperations}
            userId={this.props.userId}
          />
        )}
      </>
    );
  }
}
